<template>
    <BaseCard padding="none" radius="lgr" elevated class="flex max-h-[min(60vh,32rem)] flex-col">
        <header class="flex shrink-0 items-start justify-between gap-3 border-b border-border p-5">
            <div class="min-w-0">
                <h2 class="body-serif text-ink">{{ plan.goal_title ? t("myDevelopment.actionProposalFor", { goal: plan.goal_title }) : t("myDevelopment.actionProposal") }}</h2>
                <p class="scaffold-body-small text-ink-muted">{{ weekLabel }}</p>
            </div>
            <BaseButton theme="ghost" size="sm" icon-only :aria-label="t('common.close')" :disabled="disabled" @click="emit('dismiss')">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </header>
        <div class="min-h-0 overflow-y-auto px-5">
            <div v-for="action in plan.actions" :key="action.action_ref" class="border-b border-border py-4 last:border-b-0">
                <BaseCheckbox
                    :model-value="selected.includes(action.action_ref)"
                    :disabled="disabled"
                    shape="square"
                    class="action-checkbox items-start"
                    @update:model-value="toggle(action.action_ref, $event === true)">
                    <span class="body-small text-ink">{{ action.title }}</span>
                    <span v-if="action.timing" class="scaffold-details-small mt-1 block text-ink-muted">{{ action.timing }}</span>
                    <span class="scaffold-details-small mt-1 flex items-center gap-2 text-ink-muted">
                        <span class="size-2 shrink-0 rounded-full" :class="action.with_nadia ? 'bg-journey-marker' : 'bg-border-strong'" aria-hidden="true"></span>
                        {{ t(action.with_nadia ? "myDevelopment.withNadia" : "myDevelopment.onYourOwn") }}
                    </span>
                </BaseCheckbox>
            </div>
        </div>
        <footer class="flex shrink-0 justify-end border-t border-border p-5">
            <BaseButton theme="primary" size="sm" :disabled="disabled || selected.length === 0" @click="commit">
                {{ selected.length ? t("myDevelopment.commitToCount", { count: selected.length }) : t("myDevelopment.commitAtLeastOne") }}
            </BaseButton>
        </footer>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import { formatDateRange } from "~vue/utils/formatDate";
import { DateTime } from "luxon";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { MyDevelopmentAction, MyDevelopmentActionPlan } from "./myDevelopmentActionPlan";

const props = defineProps<{ plan: MyDevelopmentActionPlan; disabled?: boolean }>();
const emit = defineEmits<{ commit: [actions: MyDevelopmentAction[]]; dismiss: [] }>();
const { t } = useI18n();
const selected = ref<string[]>([]);
const weekLabel = computed(() => formatDateRange(props.plan.week_start, DateTime.fromISO(props.plan.week_start).plus({ days: 6 }).toISODate(), "month-day", { year: "never" }));
const toggle = (reference: string, checked: boolean) => {
    selected.value = checked ? [...selected.value, reference] : selected.value.filter((value) => value !== reference);
};
const commit = () =>
    emit(
        "commit",
        props.plan.actions.filter((action) => selected.value.includes(action.action_ref)),
    );
</script>

<style scoped>
.action-checkbox {
    --color-control-accent: var(--tonal-neutral-800);
}
</style>
